import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Download } from "lucide-react";
import BulletsDayDetail, { BulletsStatsTable } from "./BulletsDayDetail";
import BulletsItemForm from "./BulletsItemForm";
import BulletsByType from "./BulletsByType";
import { SiteFilterSelect } from "./shared/SiteFilterSelect";
import { downloadExport } from "./shared/downloadExport";
import { NoAccess } from "../equipment/shared/NoAccess";
import { PaginationBar } from "@/components/shared/PaginationBar";

const HO_ROLES = ["Director", "Manager", "Back Office"];
const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });

function SeView() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [form, setForm] = useState(null);
  const [viewDay, setViewDay] = useState(null);

  const loadToday = () => {
    client.get("/bullets-register/today").then((res) => setToday(res.data)).catch((err) => toast.error(getErrorMessage(err)));
  };

  const loadHistory = (pageNum = 1) => {
    client
      .get("/bullets-register/history", { params: { page: pageNum, page_size: 50 } })
      .then((res) => {
        setHistory(res.data.items);
        setHistoryTotalPages(res.data.total_pages);
        setHistoryPage(res.data.page);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadToday();
    loadHistory(1);
  }, []);

  if (!today) return <p className="py-8 text-center text-slate-400">Loading...</p>;

  return (
    <div className="space-y-6" data-testid="bullets-se-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">
          Site: <span className="font-medium text-slate-700">{today.display_label}</span> — Today, {new Date().toLocaleDateString("en-IN")}
        </p>
        <div className="flex gap-2">
          <Button data-testid="bullets-se-add-received" onClick={() => setForm({ kind: "received" })} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Received
          </Button>
          <Button data-testid="bullets-se-add-replacement" onClick={() => setForm({ kind: "replacement" })} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Replacement
          </Button>
        </div>
      </div>

      <BulletsStatsTable stats={today.stats} />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Today's Received ({today.received_items.length})</h3>
          {today.received_items.length === 0 ? (
            <p className="text-sm text-slate-400">No entries yet.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {today.received_items.map((it) => (
                <li key={it.item_id} className="flex justify-between" data-testid={`bullets-se-received-${it.item_id}`}>
                  <span className="text-xs">{it.bullet_type}</span>
                  <span className="font-mono">{fmt(it.quantity)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Today's Replacements ({today.replacement_items.length})</h3>
          {today.replacement_items.length === 0 ? (
            <p className="text-sm text-slate-400">No entries yet.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {today.replacement_items.map((it) => (
                <li key={it.item_id} className="flex justify-between" data-testid={`bullets-se-replacement-${it.item_id}`}>
                  <span className="text-xs">
                    {it.bullet_type} — {it.tool_used}
                  </span>
                  <span className="font-mono">{fmt(it.number_of_bullets)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">History (read-only)</h3>
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <Table data-testid="bullets-se-history-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Received (all types)</TableHead>
                <TableHead>Replaced (all types)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-6 text-slate-400">
                    No past entries.
                  </TableCell>
                </TableRow>
              ) : (
                history.map((d) => {
                  const types = Object.keys(d.stats || {});
                  const received = types.reduce((a, t) => a + (d.stats[t]?.received_today || 0), 0);
                  const replaced = types.reduce((a, t) => a + (d.stats[t]?.replaced_today || 0), 0);
                  return (
                    <TableRow key={d.id} data-testid={`bullets-se-history-row-${d.id}`} className="cursor-pointer" onClick={() => setViewDay(d.id)}>
                      <TableCell>{new Date(d.date).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell className="font-mono">{fmt(received)}</TableCell>
                      <TableCell className="font-mono">{fmt(replaced)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <PaginationBar page={historyPage} totalPages={historyTotalPages} onPageChange={loadHistory} testId="bullets-se-history-pagination" />
      </div>

      {form && (
        <BulletsItemForm
          kind={form.kind}
          dayId={null}
          initial={null}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            loadToday();
          }}
        />
      )}
      {viewDay && <BulletsDayDetail dayId={viewDay} onClose={() => setViewDay(null)} />}
    </div>
  );
}

function HoView() {
  const [view, setView] = useState("byDate");
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDay, setViewDay] = useState(null);
  const [manualForm, setManualForm] = useState(false);

  useEffect(() => {
    client.get("/bullets-register/sites").then((res) => setSites(res.data)).catch(() => {});
  }, []);

  const loadRegister = () => {
    if (!site) {
      setDays([]);
      return;
    }
    setLoading(true);
    client
      .get("/bullets-register/register", { params: { site } })
      .then((res) => setDays(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadRegister, [site]);

  const exportByDate = async () => {
    try {
      await downloadExport("/bullets-register/export/by-date", { site });
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="bullets-ho-view">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5">
          <button
            data-testid="bullets-view-by-date"
            onClick={() => setView("byDate")}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === "byDate" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            By Date
          </button>
          <button
            data-testid="bullets-view-by-type"
            onClick={() => setView("byType")}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === "byType" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            By Type
          </button>
        </div>
        <SiteFilterSelect sites={sites} value={site} onChange={setSite} testId="bullets-site-filter" placeholder="Select site" />
        {view === "byDate" && site && (
          <Button variant="outline" onClick={exportByDate} data-testid="bullets-export-by-date">
            <Download className="h-4 w-4 mr-1.5" /> Export Excel
          </Button>
        )}
        <Button data-testid="bullets-ho-add-entry" onClick={() => setManualForm(true)} className="bg-amber-600 hover:bg-amber-700 ml-auto">
          <Plus className="h-4 w-4 mr-1.5" /> Add entry (HO)
        </Button>
      </div>

      {view === "byType" && <BulletsByType site={site} />}
      {view === "byDate" && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <Table data-testid="bullets-register-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>25mm Balance</TableHead>
                <TableHead>32mm Balance</TableHead>
                <TableHead>Roller Bits Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!site ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                    Select a site to view its Bullets Register.
                  </TableCell>
                </TableRow>
              ) : loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : days.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                    No entries found for this site.
                  </TableCell>
                </TableRow>
              ) : (
                days.map((d) => (
                  <TableRow key={d.id} data-testid={`bullets-register-row-${d.id}`} className="cursor-pointer" onClick={() => setViewDay(d.id)}>
                    <TableCell>{new Date(d.date).toLocaleDateString("en-IN")}</TableCell>
                    {["25mm", "32mm", "Roller Bits"].map((t) => {
                      const bal = d.stats?.[t]?.balance ?? 0;
                      return (
                        <TableCell key={t} className={`font-mono ${bal < 0 ? "text-red-600" : ""}`}>
                          {fmt(bal)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {viewDay && <BulletsDayDetail dayId={viewDay} onClose={() => setViewDay(null)} onChanged={loadRegister} />}
      {manualForm && (
        <BulletsItemForm
          manual
          sites={sites}
          kind="received"
          dayId={null}
          initial={null}
          onClose={() => setManualForm(false)}
          onSaved={(data) => {
            setManualForm(false);
            if (data.site === site) loadRegister();
            else setSite(data.site);
          }}
        />
      )}
    </div>
  );
}

export default function BulletsRegisterTab() {
  const { user } = useAuth();
  const isSE = user?.role === "Site Engineer";
  const isHO = HO_ROLES.includes(user?.role);
  if (!isSE && !isHO) return <NoAccess />;
  return <div data-testid="bullets-register-tab">{isSE ? <SeView /> : <HoView />}</div>;
}
